"use client";

import { useCallback, useEffect, useState } from "react";
import type { BrandConfig } from "@/types/branding";
import { DEFAULT_BRAND_CONFIG, DEFAULT_BRAND_KEY } from "@/types/branding";

const CACHE_KEY_PREFIX = "brand_config_";

function getCacheKey(key: string): string {
  return `${CACHE_KEY_PREFIX}${key}`;
}

function getCachedConfig(key: string): BrandConfig | null {
  if (globalThis.window === undefined) return null;
  try {
    const raw = globalThis.localStorage.getItem(getCacheKey(key));
    if (!raw) return null;
    return JSON.parse(raw) as BrandConfig;
  } catch {
    return null;
  }
}

function setCachedConfig(key: string, config: BrandConfig): void {
  if (globalThis.window === undefined) return;
  try {
    globalThis.localStorage.setItem(getCacheKey(key), JSON.stringify(config));
  } catch {
    // ignore
  }
}

// Module-level dedup: one in-flight request per brand key shared across all hook instances
const inflightRequests = new Map<string, Promise<BrandConfig>>();

async function fetchBrandConfig(key: string): Promise<BrandConfig> {
  const existing = inflightRequests.get(key);
  if (existing) return existing;

  const promise = (async () => {
    try {
      const res = await fetch(`/api/brands/${encodeURIComponent(key)}`);
      if (!res.ok) {
        const cached = getCachedConfig(key);
        return cached?.key === key ? cached : DEFAULT_BRAND_CONFIG;
      }
      const data = (await res.json()) as BrandConfig;
      setCachedConfig(key, data);
      return data;
    } catch {
      const cached = getCachedConfig(key);
      return cached?.key === key ? cached : DEFAULT_BRAND_CONFIG;
    } finally {
      inflightRequests.delete(key);
    }
  })();

  inflightRequests.set(key, promise);
  return promise;
}

export function useBrandConfig(brandKey: string): {
  config: BrandConfig | null;
  loading: boolean;
} {
  const [config, setConfig] = useState<BrandConfig | null>(() => {
    if (brandKey === DEFAULT_BRAND_KEY) return DEFAULT_BRAND_CONFIG;
    const cached = getCachedConfig(brandKey);
    return cached?.key === brandKey ? cached : null;
  });
  const [loading, setLoading] = useState(() => {
    if (brandKey === DEFAULT_BRAND_KEY) return false;
    const cached = getCachedConfig(brandKey);
    return cached?.key !== brandKey;
  });

  const doFetch = useCallback(async (key: string) => {
    if (key === DEFAULT_BRAND_KEY) {
      setConfig(DEFAULT_BRAND_CONFIG);
      setLoading(false);
      return;
    }
    setLoading(true);
    const data = await fetchBrandConfig(key);
    setConfig(data);
    setLoading(false);
  }, []);

  useEffect(() => {
    doFetch(brandKey);
  }, [brandKey, doFetch]);

  return { config, loading };
}
